import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, IconButton, Menu, MenuItem as MuiMenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { Chip } from '@mui/material';
import { RootState } from '../../../store';
import { GroupsTeamsService } from '../../../services/groups-teams.service';
import {
    GT_COLORS,
    StatCard,
    DenseTable,
    TagPills,
    SearchDropdown,
    WatchButton,
    isWatchingGroup,
} from '../../../components/groups-teams/shared';
import type { DenseColumn } from '../../../components/groups-teams/shared';
import type { L1Group } from '../../../store/groups-teams.store';
import { CreateGroupModal } from './modals/CreateGroupModal';
import { DeleteGroupModal } from './modals/DeleteGroupModal';
import { UnassignedUsersDrawer } from './modals/UnassignedUsersDrawer';
import { pluralize } from '../../../helpers/pluralize';
import { permissionCheck } from '../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../constants/systemPermissions.constants';
import { TagPicker } from '../../../components/groups-teams-dashboard/tag-picker';
import type { TagOption } from '../reports/groups-teams-activity/types';
import { toast } from 'react-toastify';

export const GroupsTeamsPage: React.FC = () => {
    const navigate = useNavigate();
    const { l1Groups, l1Stats, loading, searchResults, mySubscriptions } = useSelector(
        (state: RootState) => state.groupsTeams
    );

    const canManageGroups = permissionCheck(customerPermissionFlags.GROUP_LEADER);

    const [createOpen, setCreateOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<L1Group | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; group: L1Group } | null>(null);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [unassignedOpen, setUnassignedOpen] = useState(false);

    useEffect(() => {
        GroupsTeamsService.fetchOverview((ok, msg) => {
            if (!ok) toast.error(msg || 'Failed to load groups.');
        });
        GroupsTeamsService.loadMySubscriptions();
    }, []);

    const handleSearch = useCallback((q: string) => {
        GroupsTeamsService.search(q);
    }, []);

    const handleDeleteGroup = () => {
        if (!deleteTarget) return;
        GroupsTeamsService.deleteGroup(deleteTarget.id, (isValid, message) => {
            if (isValid) {
                toast.success(`${deleteTarget.name} deleted.`);
                setDeleteTarget(null);
            } else {
                toast.error(message || 'Failed to delete group.');
            }
        });
    };

    // Collect all unique tags with counts for the TagPicker
    const tagCountMap = new Map<string, number>();
    l1Groups.forEach((g) => g.tags.forEach((t) => tagCountMap.set(t, (tagCountMap.get(t) || 0) + 1)));
    const tagOptions: TagOption[] = Array.from(tagCountMap.entries()).map(([name, count]) => ({ name, count }));

    const filteredGroups = selectedTags.length === 0
        ? l1Groups
        : l1Groups.filter((g) => selectedTags.some((t) => g.tags.includes(t)));

    const formatLeaders = (leaders: L1Group['leaders']) => {
        if (!leaders || leaders.length === 0) return <Typography sx={{ color: GT_COLORS.textDisabled }}>—</Typography>;
        const names = leaders.map((l) => {
            if (l.firstName && l.lastName) return `${l.firstName[0]}. ${l.lastName}`;
            if (l.fullname) return l.fullname;
            return l.email || 'Unknown';
        });
        if (names.length <= 2) return <Typography sx={{ color: GT_COLORS.textMuted, fontSize: '14px' }}>{names.join(', ')}</Typography>;
        return (
            <Typography sx={{ color: GT_COLORS.textMuted, fontSize: '14px' }}>
                {names.slice(0, 2).join(', ')} +{names.length - 2}
            </Typography>
        );
    };

    const columns: DenseColumn<L1Group>[] = [
        {
            id: 'name',
            label: 'Group Name',
            sortable: true,
            getSortValue: (r) => r.name,
            render: (r) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>{r.name}</strong>
                    {r.teamCount === 0 && (
                        <Chip
                            label="Needs Setup"
                            size="small"
                            sx={{
                                fontSize: '11px',
                                height: 20,
                                fontWeight: 500,
                                backgroundColor: GT_COLORS.amberLight,
                                color: GT_COLORS.amber,
                            }}
                        />
                    )}
                </Box>
            ),
        },
        {
            id: 'teams',
            label: 'Teams',
            sortable: true,
            getSortValue: (r) => r.teamCount,
            render: (r) => r.teamCount,
        },
        {
            id: 'members',
            label: 'Members',
            sortable: true,
            getSortValue: (r) => r.memberCount,
            render: (r) => r.memberCount.toLocaleString(),
        },
        {
            id: 'tags',
            label: 'Tags',
            render: (r) => <TagPills tags={r.tags} max={2} />,
        },
        {
            id: 'leaders',
            label: 'Leaders',
            render: (r) => formatLeaders(r.leaders),
        },
        {
            id: 'alerts',
            label: '',
            width: 90,
            align: 'center',
            render: (r) => {
                const watching = isWatchingGroup(mySubscriptions, r.id, r.teamIds);
                return (
                    <WatchButton
                        watching={watching}
                        onClick={() => {
                            GroupsTeamsService.toggleSubscription('group', r.id, watching, (ok, msg) => {
                                if (ok) toast.success(watching ? 'Unsubscribed from alerts.' : 'Subscribed to alerts!');
                                else toast.error(msg || 'Failed to update subscription.');
                            }, { teamIds: r.teamIds });
                        }}
                    />
                );
            },
        },
        ...(canManageGroups ? [{
            id: 'actions',
            label: '',
            width: 48,
            align: 'center' as const,
            render: (r: L1Group) => (
                <IconButton
                    size="small"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        setMenuAnchor({ el: e.currentTarget, group: r });
                    }}
                    sx={{ color: GT_COLORS.textFaint }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            ),
        }] : []),
    ];

    return (
        <Box sx={{ px: { xs: 2, md: 3 } }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '20px' }}>
                <Box>
                    <Typography sx={{ fontSize: '24px', fontWeight: 600, color: GT_COLORS.textPrimary, lineHeight: 1.2 }}>
                        Groups & Teams
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, mt: '3px' }}>
                        Manage your organizational structure, team assignments, and feature configuration.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '10px' }}>
                    {canManageGroups && (
                        <Button
                            variant="outlined"
                            startIcon={<LocalOfferOutlinedIcon />}
                            onClick={() => navigate('/groups-teams/tag-explorer')}
                            sx={{
                                color: GT_COLORS.primary,
                                borderColor: GT_COLORS.border,
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                '&:hover': { borderColor: GT_COLORS.primary, backgroundColor: GT_COLORS.primaryLight },
                            }}
                        >
                            Tag Explorer
                        </Button>
                    )}
                    {canManageGroups && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateOpen(true)}
                            sx={{
                                backgroundColor: GT_COLORS.primary,
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                            }}
                        >
                            Create Group
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Stats */}
            <Box sx={{ backgroundColor: GT_COLORS.pageBg, borderRadius: '10px', padding: '12px', mb: '20px' }}>
                <Box sx={{ display: 'flex', gap: '12px' }}>
                    <StatCard value={l1Stats.groups} label="Groups" />
                    <StatCard value={l1Stats.teams} label="Teams" />
                    <StatCard value={l1Stats.pomUsers} label="POM Users" />
                    <StatCard value={l1Stats.unassigned} label="Unassigned Users" onClick={() => setUnassignedOpen(true)} valueColor={l1Stats.unassigned > 0 ? GT_COLORS.amber : undefined} />
                </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '16px', flexWrap: 'wrap' }}>
                <SearchDropdown
                    placeholder="Search groups, teams, or people..."
                    groups={(searchResults.groups || []).map((g: any) => ({
                        id: g.id,
                        name: g.name || '',
                        teamCount: g.teamCount || 0,
                    }))}
                    people={searchResults.people.map((p: any) => ({
                        id: p.id,
                        name: `${p.firstName || ''} ${p.lastName || ''}`.trim(),
                        subtitle: [p.phoneNumber, p.employeeId].filter(Boolean).join(' · '),
                        teamName: p.teamName,
                        groupName: p.groupName,
                        initials: `${(p.firstName || '?')[0]}${(p.lastName || '?')[0]}`.toUpperCase(),
                        avatarColor: GT_COLORS.primary,
                        unassigned: !p.teamId,
                    }))}
                    teams={searchResults.teams.map((t: any) => ({
                        id: t.id,
                        name: t.name || '',
                        memberCount: (t.membersIds || []).length,
                        groupName: t.groupName || '',
                    }))}
                    loading={loading.search}
                    onSearch={handleSearch}
                    onSelectGroup={(g) => {
                        navigate(`/groups-teams/${g.id}`);
                    }}
                    onSelectPerson={(p) => {
                        const raw = searchResults.people.find((r: any) => r.id === p.id);
                        if (raw?.groupId && raw?.teamId) {
                            navigate(`/groups-teams/${raw.groupId}/${raw.teamId}`);
                        } else if (raw?.groupId) {
                            navigate(`/groups-teams/${raw.groupId}`);
                        }
                    }}
                    onSelectTeam={(t) => {
                        const raw = searchResults.teams.find((r: any) => r.id === t.id);
                        if (raw?.groupId) {
                            navigate(`/groups-teams/${raw.groupId}/${raw.id}`);
                        }
                    }}
                />
                <TagPicker
                    options={tagOptions}
                    selected={selectedTags}
                    onChange={setSelectedTags}
                    placeholder="Filter by tags…"
                    minWidth={200}
                />
            </Box>

            {/* Table */}
            <DenseTable
                columns={columns}
                rows={filteredGroups}
                getRowKey={(r) => r.id}
                onRowClick={(r) => navigate(`/groups-teams/${r.id}`)}
                defaultSortColumn="name"
                emptyIcon={<AccountTreeOutlinedIcon sx={{ fontSize: 48 }} />}
                emptyMessage="No groups yet. Create one to get started."
            />

            {/* Context Menu */}
            <Menu
                anchorEl={menuAnchor?.el}
                open={!!menuAnchor}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 160, borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,.12)' } } }}
            >
                <MuiMenuItem
                    onClick={() => {
                        if (menuAnchor) {
                            setDeleteTarget(menuAnchor.group);
                            setMenuAnchor(null);
                        }
                    }}
                    sx={{ fontSize: '14px', color: GT_COLORS.red, gap: 1 }}
                >
                    <DeleteOutlineIcon fontSize="small" /> Delete Group
                </MuiMenuItem>
            </Menu>

            {/* Modals */}
            <CreateGroupModal open={createOpen} onClose={() => setCreateOpen(false)} />
            <DeleteGroupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDeleteGroup}
                groupName={deleteTarget?.name || ''}
                teamCount={deleteTarget?.teamCount || 0}
                loading={loading.saving}
            />
            <UnassignedUsersDrawer open={unassignedOpen} onClose={() => setUnassignedOpen(false)} />
        </Box>
    );
};
